import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import DOMPurify from 'dompurify';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Heading2, Heading3, List, ListOrdered, Quote, Code,
  Link2, Undo2, Redo2,
} from 'lucide-react';

/* ─────────────────────────────────────────────────────────────
   StuiRichText — canonical STUI rich text editor (TipTap/ProseMirror,
   headless; all chrome styled via STUI.css .stui-rte-* classes).

   <StuiRichText value={html} onChange={setHtml} placeholder="..." />

   StuiRichTextViewer — the ONLY sanctioned way to render stored
   rich text HTML. Sanitizes with DOMPurify before injecting.

   <StuiRichTextViewer html={storedHtml} />
─────────────────────────────────────────────────────────────── */

const ToolbarButton = ({ icon: Icon, title, active, disabled, onClick }) => (
  <button
    type="button"
    className={`stui-rte-btn${active ? ' active' : ''}`}
    title={title}
    disabled={disabled}
    onMouseDown={e => e.preventDefault()}
    onClick={onClick}
  >
    <Icon size={15} />
  </button>
);

const StuiRichText = ({ value = '', onChange, placeholder = '', minHeight = 200 }) => {
  const [linkBarOpen, setLinkBarOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  // Tracks the last HTML this editor emitted. TipTap normalizes markup
  // (e.g. <li>x</li> → <li><p>x</p></li>), so the incoming `value` prop and
  // editor.getHTML() can disagree forever — without this guard the external
  // sync below and onUpdate can ping-pong into a render loop.
  const lastEmitted = useRef(null);

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        // v3 StarterKit bundles link + underline — disable in favor of
        // the standalone extensions configured below
        link: false,
        underline: false,
      }),
      Underline,
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      lastEmitted.current = html;
      if (onChange) onChange(html);
    },
  });

  // Sync EXTERNAL value changes into the editor without nuking the cursor.
  // Skip when the value is just our own emission echoed back through parent state.
  useEffect(() => {
    if (!editor) return;
    if ((value || '') === lastEmitted.current) return;
    const current = editor.getHTML();
    if ((value || '') !== current) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  const openLinkBar = useCallback(() => {
    if (!editor) return;
    setLinkUrl(editor.getAttributes('link').href || '');
    setLinkBarOpen(open => !open);
  }, [editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const url = linkUrl.trim();
    if (url) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    } else {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    }
    setLinkBarOpen(false);
    setLinkUrl('');
  }, [editor, linkUrl]);

  const clearLink = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setLinkBarOpen(false);
    setLinkUrl('');
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="stui-rte">
      <div className="stui-rte-toolbar">
        <ToolbarButton icon={Bold} title="Bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} />
        <ToolbarButton icon={Italic} title="Italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} />
        <ToolbarButton icon={UnderlineIcon} title="Underline" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} />
        <ToolbarButton icon={Strikethrough} title="Strikethrough" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} />
        <span className="stui-rte-divider" />
        <ToolbarButton icon={Heading2} title="Heading 2" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} />
        <ToolbarButton icon={Heading3} title="Heading 3" active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} />
        <span className="stui-rte-divider" />
        <ToolbarButton icon={List} title="Bullet list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} />
        <ToolbarButton icon={ListOrdered} title="Ordered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
        <span className="stui-rte-divider" />
        <ToolbarButton icon={Quote} title="Blockquote" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} />
        <ToolbarButton icon={Code} title="Code" active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()} />
        <ToolbarButton icon={Link2} title="Link" active={editor.isActive('link') || linkBarOpen} onClick={openLinkBar} />
        <span className="stui-rte-divider" />
        <ToolbarButton icon={Undo2} title="Undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()} />
        <ToolbarButton icon={Redo2} title="Redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()} />
      </div>
      {linkBarOpen && (
        <div className="stui-rte-linkbar">
          <input
            type="text"
            placeholder="https://..."
            value={linkUrl}
            onChange={e => setLinkUrl(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); setLink(); } }}
            autoFocus
          />
          <button type="button" onMouseDown={e => e.preventDefault()} onClick={setLink}>Set</button>
          <button type="button" onMouseDown={e => e.preventDefault()} onClick={clearLink}>Clear</button>
        </div>
      )}
      <div className="stui-rte-editor" style={{ minHeight }}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

/* Read-only renderer — the ONLY sanctioned way to render stored rich text */
export const StuiRichTextViewer = ({ html }) => (
  <div
    className="stui-rte-content"
    dangerouslySetInnerHTML={{
      __html: DOMPurify.sanitize(html || '', { USE_PROFILES: { html: true } }),
    }}
  />
);

export default StuiRichText;
